import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Info, SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { NumberField } from '@/components/ui/number-field';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { BUSINESS_METRICS, EVIDENCE_STRENGTH_LABEL, LEVERS, RETENTION_NOTE, formatMoney } from '../data/model';
import type {
  BusinessMetricInputs,
  EvidenceStrength,
  LeverEvidence,
  RoiLeverId,
  RoiLeverInputs,
  RoiLeverVerdict,
  RoiResult,
} from '../data/model';
import { RoiBarChart } from '../components/RoiBarChart';

interface Props {
  leverInputs: RoiLeverInputs;
  onLeverInputChange: (id: RoiLeverId, field: 'then' | 'now', value: number) => void;
  businessMetricInputs: BusinessMetricInputs;
  result: RoiResult;
  onEditCompanyNumbers: () => void;
  /** True in sales/demo mode (PEAK-271): figures are fixed, so inputs are read-only. */
  disabled?: boolean;
}

const strengthBadgeVariant = (strength: EvidenceStrength) =>
  strength === 'strong' ? 'success' : strength === 'good_indirect' ? 'warning' : 'muted';

const verdictCopy = (verdict: RoiLeverVerdict, range: [number, number], unit: string): string => {
  if (verdict === 'above_range') return 'At or above the top of the range research supports.';
  if (verdict === 'in_range') return `Within the range research supports (${range[0]}-${range[1]}${unit}).`;
  return `Below the range research supports (${range[0]}-${range[1]}${unit}); may need more time or engagement.`;
};

const EvidencePanel: React.FC<{ evidence: LeverEvidence }> = ({ evidence }) => (
  <div className='mt-2 rounded-md bg-muted/50 p-3 text-xs leading-relaxed text-muted-foreground'>
    <p>
      <span className='font-medium text-foreground'>Study finding: </span>
      {evidence.studyFinding}
    </p>
    <p className='mt-2'>
      <span className='font-medium text-foreground'>Our bridging assumption: </span>
      {evidence.bridgingAssumption}
    </p>
    <p className='mt-2'>
      <span className='font-medium text-foreground'>Formula: </span>
      {evidence.formula}
    </p>
    {evidence.overlapNote && (
      <p className='mt-2'>
        <span className='font-medium text-foreground'>Overlap note: </span>
        {evidence.overlapNote}
      </p>
    )}
  </div>
);

const EvidenceToggle: React.FC<{
  id: string;
  evidence: LeverEvidence;
  open: boolean;
  onToggle: (id: string) => void;
}> = ({ id, evidence, open, onToggle }) => (
  <Collapsible open={open} onOpenChange={() => onToggle(id)}>
    <CollapsibleTrigger asChild>
      <Button variant='ghost' size='sm' className='-ml-2 h-7 px-2 text-xs'>
        {open ? <ChevronUp /> : <ChevronDown />}
        {open ? 'Hide the evidence' : 'Show the evidence'}
      </Button>
    </CollapsibleTrigger>
    <CollapsibleContent>
      <EvidencePanel evidence={evidence} />
    </CollapsibleContent>
  </Collapsible>
);

const StatCard: React.FC<{ label: string; value: string; tooltip: string }> = ({ label, value, tooltip }) => (
  <Card>
    <CardContent className='pt-5'>
      <div className='flex items-center gap-1'>
        <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>{label}</p>
        <Tooltip>
          <TooltipTrigger asChild>
            <button type='button' className='text-muted-foreground hover:text-foreground' aria-label={`About ${label}`}>
              <Info className='size-3' />
            </button>
          </TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      </div>
      <p className='mt-1 text-2xl font-semibold'>{value}</p>
    </CardContent>
  </Card>
);

export const SavingsSection: React.FC<Props> = ({
  leverInputs,
  onLeverInputChange,
  businessMetricInputs,
  result,
  onEditCompanyNumbers,
  disabled = false,
}) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const chartData = [
    ...LEVERS.map((lever) => ({
      label: lever.title,
      value: Math.round(result.levers.find((l) => l.id === lever.id)?.value ?? 0),
    })),
    ...BUSINESS_METRICS.map((metric) => ({
      label: metric.title,
      value: Math.round(result.businessMetrics.find((m) => m.id === metric.id)?.saving ?? 0),
    })),
  ];

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <div>
          <h2 className='text-base font-semibold'>Projected ROI</h2>
          <p className='text-sm text-muted-foreground'>
            Enter each metric's cohort average "then" and "now"; each is priced from one published study plus a stated
            bridging assumption.
          </p>
        </div>
        <Button variant='outline' size='sm' onClick={onEditCompanyNumbers}>
          <SlidersHorizontal />
          Edit company numbers
        </Button>
      </div>

      {/* Headline figures first — they were previously stranded below every
          lever card in two part-filled rows. */}
      <TooltipProvider delayDuration={200}>
        <div className='grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7'>
          <StatCard
            label='Confirmed savings'
            value={formatMoney(result.confirmedSavingsTotal)}
            tooltip='From your reported business metrics (then vs now total cost) — actual company data, not an estimate.'
          />
          <StatCard
            label='Estimated savings'
            value={formatMoney(result.estimatedSavingsTotal)}
            tooltip='Modelled from biometric improvements using published research plus a stated bridging assumption.'
          />
          <StatCard
            label='Total savings'
            value={formatMoney(result.totalSavingsTotal)}
            tooltip='Confirmed savings + estimated savings.'
          />
          <StatCard
            label='Programme cost'
            value={formatMoney(result.programmeCostTotal)}
            tooltip='How much is invested in the programme.'
          />
          <StatCard label='Net value' value={formatMoney(result.netValue)} tooltip='Total savings − Programme cost.' />
          <StatCard
            label='Return per £1 spent'
            value={`£${result.returnPerPoundSpent.toFixed(2)}`}
            tooltip='Total savings ÷ Programme cost.'
          />
          <StatCard
            label='Upside if you reach top of range'
            value={formatMoney(result.upside)}
            tooltip='Extra estimated savings available if every biometric lever reached the top of its research range.'
          />
        </div>
      </TooltipProvider>

      <Card>
        <CardHeader>
          <CardTitle>Where the value comes from</CardTitle>
        </CardHeader>
        <CardContent>
          <RoiBarChart data={chartData} />
        </CardContent>
      </Card>

      {/* Two columns of levers: each card is a pair of short number fields and a
          one-line verdict, so full-width rows wasted most of the viewport. */}
      <div className='grid grid-cols-1 gap-4 xl:grid-cols-2'>
        {LEVERS.map((lever) => {
          const leverResult = result.levers.find((l) => l.id === lever.id)!;
          const { then, now } = leverInputs[lever.id];

          return (
            <Card key={lever.id}>
              <CardHeader>
                <div className='flex items-center justify-between gap-2'>
                  <CardTitle>{lever.title}</CardTitle>
                  <Badge variant={strengthBadgeVariant(lever.evidenceStrength)}>
                    {EVIDENCE_STRENGTH_LABEL[lever.evidenceStrength]}
                  </Badge>
                </div>
                <CardDescription>{lever.subtitle}</CardDescription>
              </CardHeader>
              <CardContent className='flex flex-col gap-3'>
                <div className='grid grid-cols-2 gap-3'>
                  <div className='flex flex-col gap-1.5'>
                    <label className='text-xs font-medium text-muted-foreground'>Then</label>
                    <NumberField
                      value={then}
                      onChange={(v) => onLeverInputChange(lever.id, 'then', v)}
                      step={0.1}
                      suffix={lever.unit}
                      disabled={disabled}
                    />
                  </div>
                  <div className='flex flex-col gap-1.5'>
                    <label className='text-xs font-medium text-muted-foreground'>Now</label>
                    <NumberField
                      value={now}
                      onChange={(v) => onLeverInputChange(lever.id, 'now', v)}
                      step={0.1}
                      suffix={lever.unit}
                      disabled={disabled}
                    />
                  </div>
                </div>

                <div className='flex items-center justify-between text-sm'>
                  <span className='text-muted-foreground'>Change achieved</span>
                  <span className='font-medium'>
                    {leverResult.changeAchieved.toFixed(1)}
                    {lever.unit} = {formatMoney(leverResult.value)}
                  </span>
                </div>

                <div
                  className={cn(
                    'rounded-md p-2.5 text-sm',
                    leverResult.verdict === 'below_range'
                      ? 'bg-warning-muted text-warning'
                      : 'bg-success-muted text-success',
                  )}
                >
                  {verdictCopy(leverResult.verdict, lever.evidenceRange, lever.unit)}
                </div>

                <EvidenceToggle
                  id={lever.id}
                  evidence={lever.evidence}
                  open={expanded.has(lever.id)}
                  onToggle={toggle}
                />
              </CardContent>
            </Card>
          );
        })}

        {BUSINESS_METRICS.map((metric) => {
          const { then, now } = businessMetricInputs[metric.id];
          const saving = result.businessMetrics.find((m) => m.id === metric.id)?.saving ?? 0;
          return (
            <Card key={metric.id}>
              <CardHeader>
                <div className='flex items-center justify-between gap-2'>
                  <CardTitle>{metric.title}</CardTitle>
                  <Badge variant='success'>Confirmed</Badge>
                </div>
                <CardDescription>{metric.subtitle}</CardDescription>
              </CardHeader>
              <CardContent className='flex flex-col gap-3'>
                <div className='grid grid-cols-2 gap-3'>
                  <div className='flex flex-col gap-1.5'>
                    <span className='text-xs font-medium text-muted-foreground'>Then (total cost)</span>
                    <span className='flex h-9 items-center font-medium'>{formatMoney(then)}</span>
                  </div>
                  <div className='flex flex-col gap-1.5'>
                    <span className='text-xs font-medium text-muted-foreground'>Now (total cost)</span>
                    <span className='flex h-9 items-center font-medium'>{formatMoney(now)}</span>
                  </div>
                </div>

                <div className='flex items-center justify-between text-sm'>
                  <span className='text-muted-foreground'>
                    {saving >= 0 ? 'Confirmed saving' : 'Confirmed increase'}
                  </span>
                  <span className='font-medium'>{formatMoney(Math.abs(saving))}</span>
                </div>
              </CardContent>
            </Card>
          );
        })}

        <Card>
          <CardHeader>
            <div className='flex items-center justify-between gap-2'>
              <CardTitle>Staff retention</CardTitle>
              <Badge variant='muted'>No evidence yet</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <p className='text-sm text-muted-foreground'>{RETENTION_NOTE}</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
